import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './ProductDisplay.css';
import { useCart } from '../../hooks/useCart';
import { useWishlist } from '../../hooks/useWishlist';
import { useAuthStore } from '../../stores/authStore';
import {
  getAllProductImages,
  getProductName,
  getProductPrices,
  getProductId,
  isOwnStoreProduct,
  calculateDiscount,
  formatPrice,
  canBuyProduct,
  canOfferProduct,
  isProductOutOfStock,
  getDisplayStock,
  findAvailableVariant,
  isVariableProductType,
} from '../../utils/helpers';
import { 
  Heart, Star, Truck, Shield, RotateCcw, ChevronRight, 
  Minus, Plus, ShoppingCart, Zap, Check, Package, Clock,
  Share2, ZoomIn, MessageCircle, Store
} from 'lucide-react';
const ProductDisplay = ({ product }) => {
  const { t } = useTranslation(['products', 'common']);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated, user } = useAuthStore();
  const { addToCart, isAddingToCart } = useCart();
  const { addToWishlist, removeFromWishlist, isInWishlist, isAddingToWishlist } = useWishlist();

  const images = getAllProductImages(product);
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedSize, setSelectedSize] = useState(null);
  const [selectedColor, setSelectedColor] = useState(0);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [activeTab, setActiveTab] = useState('description');
  const [isZoomed, setIsZoomed] = useState(false);
  const [addedToCart, setAddedToCart] = useState(false);
  const productId = product ? getProductId(product) : null;

  const isVariableProduct = Boolean(
    product &&
      isVariableProductType(product.productType) &&
      product.variants?.length > 0
  );

  const colors = isVariableProduct
    ? [...new Set(product.variants.map((v) => v.color).filter(Boolean))].map((c) => ({
        name: c,
        hex: c,
      }))
    : product?.availableColors?.map((c) => ({ name: c, hex: c })) || product?.colors || [];

  const sizes = isVariableProduct
    ? [...new Set(product.variants.map((v) => v.size).filter(Boolean))]
    : product?.availableSizes || product?.sizes || [];

  const variantCfg = product?.variantConfig;
  const needsSize =
    isVariableProduct && (variantCfg?.hasSizes ?? sizes.length > 0);
  const needsColor =
    isVariableProduct && (variantCfg?.hasColors ?? colors.length > 0);

  useEffect(() => {
    if (!product || !isVariableProduct) return;
    const colorName = needsColor ? colors[selectedColor]?.name : undefined;
    if (needsSize && !selectedSize) {
      setSelectedVariant(null);
      return;
    }
    const match = findAvailableVariant(
      product,
      needsSize ? selectedSize : undefined,
      colorName
    );
    setSelectedVariant(match);
  }, [product, isVariableProduct, selectedSize, selectedColor, colors, needsSize, needsColor]);

  const ownsOwnStore = product ? isOwnStoreProduct(product, user) : false;

  useEffect(() => {
    if (searchParams.get('openBidChat') === 'true' && productId && isAuthenticated && product) {
      if (ownsOwnStore) {
        setSearchParams({}, { replace: true });
        return;
      }
      navigate('/conversations', {
        replace: true,
        state: { productId, openOffer: true },
      });
    }
  }, [searchParams, productId, isAuthenticated, ownsOwnStore, product, navigate, setSearchParams]);

  if (!product) {
    return <div className="product-display-loading">{t('loading')}</div>;
  }

  const productName = getProductName(product);
  
  // For variable products, use variant prices; for simple, use product prices
  let price, comparePrice;
  if (isVariableProduct && selectedVariant) {
    price = selectedVariant.price || 0;
    comparePrice = selectedVariant.originalPrice || selectedVariant.price || 0;
  } else if (isVariableProduct && product?.priceRange) {
    // Show price range if no variant selected
    price = product.priceRange.min;
    comparePrice = product.priceRange.max;
  } else {
    const prices = getProductPrices(product);
    price = prices.price;
    comparePrice = prices.comparePrice;
  }
  const discount = calculateDiscount(comparePrice, price);

  const isFavorite = isInWishlist(productId);

  const stockQty = getDisplayStock(product, selectedVariant);
  const productOutOfStock = isProductOutOfStock(product);
  const canMakeOffer = canOfferProduct(product) && !ownsOwnStore;
  const inStock = !productOutOfStock && stockQty > 0;
  const hasRequiredSize = !needsSize || Boolean(selectedSize);
  const hasRequiredColor = !needsColor || colors.length === 0 || Boolean(colors[selectedColor]);
  const canPurchase =
    canBuyProduct(product) &&
    inStock &&
    hasRequiredSize &&
    hasRequiredColor &&
    (!isVariableProduct || (selectedVariant && stockQty > 0));

  const toggleFavorite = async () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    if (isFavorite) {
      await removeFromWishlist(productId);
    } else {
      await addToWishlist(productId);
    }
  };

  const handleAddToCart = async () => {
    if (!canPurchase) {
      return;
    }
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    // For variable products, require variant selection
    if (isVariableProduct) {
      if (needsSize && !selectedSize) {
        alert(t('selectSize'));
        return;
      }
      if (needsColor && colors.length > 0 && !colors[selectedColor]) {
        alert(t('selectColor'));
        return;
      }

      const matchingVariant = selectedVariant;
      if (!matchingVariant) {
        alert(t('variantNotAvailable', { defaultValue: 'Selected variant is not available' }));
        return;
      }

      try {
        await addToCart({
          productId,
          quantity,
          sku: matchingVariant.sku,
          variantId: matchingVariant.id || matchingVariant._id,
          size: matchingVariant.size,
          color: matchingVariant.color,
        });
        setAddedToCart(true);
        setTimeout(() => setAddedToCart(false), 3000);
      } catch (error) {
        console.error('Failed to add to cart:', error);
        alert(error.message || t('failedToAddToCart'));
      }
    } else {
      // Simple product - no SKU needed
      try {
        await addToCart({ productId, quantity, sku: null });
        setAddedToCart(true);
        setTimeout(() => setAddedToCart(false), 3000);
      } catch (error) {
        console.error('Failed to add to cart:', error);
        alert(error.message || t('failedToAddToCart'));
      }
    }
  };

  const handleBuyNow = async () => {
    if (!canPurchase) return;
    await handleAddToCart();
    navigate('/checkout');
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: productName,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert(t('linkCopied', { defaultValue: 'Link copied to clipboard!', ns: 'common' }));
    }
  };

  return (
    <div className="product-display">
      {/* Breadcrumb */}
      <nav className="pd-breadcrumb">
        <Link to="/">{t('home', { defaultValue: 'Home', ns: 'common' })}</Link>
        <ChevronRight size={14} />
        <Link to="/search">{t('shop', { defaultValue: 'Shop', ns: 'common' })}</Link>
        <ChevronRight size={14} />
        <span className="current" title={productName}>
          {productName.length > 30 ? `${productName.substring(0, 30)}...` : productName}
        </span>
      </nav>

      <div className="pd-container">
        {/* Image Gallery */}
        <div className="pd-gallery">
          <div className="pd-thumbnails">
            {images.map((img, idx) => (
              <button
                key={idx}
                className={`pd-thumb ${selectedImage === idx ? 'active' : ''}`}
                onClick={() => setSelectedImage(idx)}
              >
                <img src={img} alt={`${productName} ${idx + 1}`} />
              </button>
            ))}
          </div>
          
          <div 
            className={`pd-main-image ${isZoomed ? 'zoomed' : ''}`}
            onClick={() => setIsZoomed(!isZoomed)}
          >
            <img src={images[selectedImage]} alt={productName} />
            {discount > 0 && (
              <span className="pd-discount-badge">-{discount}%</span>
            )}
            <button className="pd-zoom-btn">
              <ZoomIn size={20} />
            </button>
          </div>
        </div>

        {/* Product Info */}
        <div className="pd-info">
          {/* Header */}
          <div className="pd-header">
            <div className="pd-actions-top">
              <button className="pd-share-btn" onClick={handleShare} title={t('share')}>
                <Share2 size={18} />
              </button>
              <button 
                className={`pd-fav-btn ${isFavorite ? 'active' : ''}`} 
                onClick={toggleFavorite}
                disabled={isAddingToWishlist}
                title={isFavorite ? t('removeFromWishlist') : t('addToWishlist')}
              >
                <Heart size={20} fill={isFavorite ? '#ef4444' : 'none'} />
              </button>
            </div>
          </div>

          <h1 className="pd-title">{productName}</h1>

          {/* Rating */}
          <div className="pd-rating">
            <div className="pd-stars">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  size={18}
                  fill={i < Math.floor(product.averageRating || product.rating || 4.5) ? '#fbbf24' : 'none'}
                  stroke={i < Math.floor(product.averageRating || product.rating || 4.5) ? '#fbbf24' : '#d1d5db'}
                />
              ))}
            </div>
            <span className="pd-rating-value">{(product.averageRating || product.rating || 4.5).toFixed(1)}</span>
            <span className="pd-rating-count">({product.reviewCount || 0} {t('reviews')})</span>
            <span className="pd-sold">{product.sold || 0} {t('sold', { ns: 'common' })}</span>
          </div>

          {/* Price */}
          <div className="pd-price-section">
            <div className="pd-price-row">
              {isVariableProduct && !selectedVariant ? (
                <span className="pd-price">
                  {product.priceRange ? `${formatPrice(product.priceRange.min)} - ${formatPrice(product.priceRange.max)}` : formatPrice(price)}
                </span>
              ) : (
                <>
                  <span className="pd-price">{formatPrice(price)}</span>
                  {comparePrice && comparePrice > price && (
                    <span className="pd-compare-price">{formatPrice(comparePrice)}</span>
                  )}
                  {discount > 0 && <span className="pd-discount-tag">{t('save', { defaultValue: 'Save', ns: 'hero' })} {discount}%</span>}
                </>
              )}
            </div>
            {isVariableProduct && selectedVariant?.sku && (
              <div className="pd-sku-info">
                <span className="pd-sku-label">{t('sku', { defaultValue: 'SKU', ns: 'common' })}:</span>
                <span className="pd-sku-value">{selectedVariant.sku}</span>
              </div>
            )}
          </div>

          {/* Stock Status */}
          <div className={`pd-stock ${canPurchase ? 'in-stock' : 'out-of-stock'}`}>
            {productOutOfStock || !canBuyProduct(product) ? (
              <>
                <Package size={16} />
                <span className="pd-availability-badge">{t('outOfStockLabel')}</span>
              </>
            ) : inStock ? (
              <>
                <Check size={16} />
                <span>{t('inStockLabel')}</span>
                <span className="pd-stock-qty">({stockQty} {t('available', { defaultValue: 'available', ns: 'common' })})</span>
              </>
            ) : (
              <>
                <Package size={16} />
                <span>{t('selectOptionsForStock', { defaultValue: 'Select size to check availability', ns: 'products' })}</span>
              </>
            )}
          </div>

          {!canPurchase && (productOutOfStock || !canBuyProduct(product)) && (
            <p className="pd-unavailable-msg" role="status">
              {t('productUnavailable', { defaultValue: 'This product is currently unavailable for purchase.', ns: 'products' })}
            </p>
          )}
          {!canPurchase && !productOutOfStock && canBuyProduct(product) && isVariableProduct && needsSize && !selectedSize && (
            <p className="pd-unavailable-msg" role="status">
              {t('selectSize')}
            </p>
          )}
          {!canPurchase && !productOutOfStock && canBuyProduct(product) && isVariableProduct && needsColor && colors.length > 0 && !colors[selectedColor] && (
            <p className="pd-unavailable-msg" role="status">
              {t('selectColor')}
            </p>
          )}

          {/* Short Description */}
          <p className="pd-short-desc">
            {product.shortDescription || product.description?.substring(0, 150) || t('defaultDescription', { defaultValue: 'Premium quality product with exceptional design and durability.', ns: 'products' })}
          </p>

          {/* Store Information */}
          {(product.owner?.storeName || product.owner?.name || product.owner?.description) && (
            <div className="pd-store-info">
              <div className="pd-store-header">
                <Store size={20} />
                <h3 className="pd-store-title">{t('storeInfo', { defaultValue: 'Store Information', ns: 'products' })}</h3>
              </div>
              <div className="pd-store-content">
                <div className="pd-store-name">
                  <span className="pd-store-label">{t('soldBy', { defaultValue: 'Sold by', ns: 'products' })}:</span>
                  <span className="pd-store-value">
                    {product.owner?.storeName || product.owner?.name || product.brand || t('officialStore', { defaultValue: 'Official Store', ns: 'bids' })}
                  </span>
                </div>
                {product.owner?.description && (
                  <p className="pd-store-description">
                    {product.owner.description}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Color Selection */}
          {needsColor && colors.length > 0 && (
            <div className="pd-option">
              <label className="pd-option-label">
                {t('color')}: <span>{colors[selectedColor]?.name}</span>
              </label>
              <div className="pd-colors">
                {colors.map((color, idx) => (
                  <button
                    key={idx}
                    className={`pd-color-btn ${selectedColor === idx ? 'active' : ''}`}
                    style={{ backgroundColor: color.hex }}
                    onClick={() => {
                      setSelectedColor(idx);
                      if (selectedSize) {
                        const match = findAvailableVariant(product, selectedSize, color.name);
                        setSelectedVariant(match);
                      }
                    }}
                    title={color.name}
                  >
                    {selectedColor === idx && <Check size={14} />}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Size Selection */}
          {needsSize && sizes.length > 0 && (
            <div className="pd-option">
              <div className="pd-option-header">
                <label className="pd-option-label">
                  {t('size')}: <span>{selectedSize || t('select', { defaultValue: 'Select', ns: 'common' })}</span>
                  {needsSize && !selectedSize && (
                    <span className="pd-required-badge">* {t('required', { defaultValue: 'Required', ns: 'common' })}</span>
                  )}
                </label>
                <Link to="/size-guide" className="pd-size-guide">{t('sizeGuide', { defaultValue: 'Size Guide', ns: 'products' })}</Link>
              </div>
              <div className="pd-sizes">
                {sizes.map((size) => {
                  const colorName = colors[selectedColor]?.name;
                  const variantWithSize = isVariableProduct
                    ? findAvailableVariant(product, size, colorName)
                    : null;
                  const isAvailable = isVariableProduct ? variantWithSize !== null : true;

                  return (
                    <button
                      key={size}
                      className={`pd-size-btn ${selectedSize === size ? 'active' : ''} ${!isAvailable ? 'out-of-stock' : ''}`}
                      onClick={() => {
                        setSelectedSize(size);
                        if (isVariableProduct) {
                          setSelectedVariant(variantWithSize);
                        }
                      }}
                      disabled={!isAvailable}
                      title={!isAvailable ? t('outOfStockLabel') : ''}
                    >
                      {size}
                      {!isAvailable && <span className="pd-size-oos">{t('oos', { defaultValue: 'OOS', ns: 'products' })}</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Quantity */}
          <div className="pd-option">
            <label className="pd-option-label">{t('quantity')}</label>
            <div className="pd-quantity">
              <button 
                className="pd-qty-btn" 
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                disabled={quantity <= 1}
              >
                <Minus size={18} />
              </button>
              <span className="pd-qty-value">{quantity}</span>
              <button 
                className="pd-qty-btn" 
                onClick={() => setQuantity(Math.min(stockQty, quantity + 1))}
                disabled={quantity >= stockQty}
              >
                <Plus size={18} />
              </button>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="pd-buttons">
            <button 
              className={`pd-btn pd-btn-cart ${addedToCart ? 'added' : ''}`}
              onClick={handleAddToCart} 
              disabled={!canPurchase || isAddingToCart}
              title={
                !canPurchase
                  ? t('outOfStockLabel')
                  : isVariableProduct && !selectedSize
                    ? t('selectSizeFirst', { defaultValue: 'Please select a size first', ns: 'products' })
                    : ''
              }
            >
              {addedToCart ? (
                <>
                  <Check size={20} />
                  {t('addedToCart')}!
                </>
              ) : isAddingToCart ? (
                t('adding', { defaultValue: 'Adding...', ns: 'bids' })
              ) : (
                <>
                  <ShoppingCart size={20} />
                  {t('addToCart')}
                </>
              )}
            </button>
            <button 
              className="pd-btn pd-btn-buy"
              onClick={handleBuyNow}
              disabled={!canPurchase || (isVariableProduct && !selectedSize)}
              title={
                !canPurchase
                  ? t('outOfStockLabel')
                  : isVariableProduct && !selectedSize
                    ? t('selectSizeFirst', { defaultValue: 'Please select a size first', ns: 'products' })
                    : ''
              }
            >
              <Zap size={20} />
              {t('buyNow')}
            </button>
            <button
              className="pd-btn pd-btn-offer"
              disabled={!canMakeOffer}
              title={
                ownsOwnStore
                  ? t('ownStoreOffer', { defaultValue: 'You cannot make offers on your own products', ns: 'conversations' })
                  : !canMakeOffer
                    ? t('outOfStockLabel')
                    : ''
              }
              onClick={() => {
                if (!canMakeOffer) return;
                if (!isAuthenticated) {
                  navigate('/login', { state: { from: `/product/${productId}` } });
                  return;
                }
                navigate('/conversations', {
                  state: { productId, openOffer: true },
                });
              }}
            >
              <MessageCircle size={20} />
              {t('makeOffer')}
            </button>
          </div>

          {/* Trust Badges */}
          <div className="pd-trust">
            <div className="pd-trust-item">
              <Truck size={20} />
              <div>
                <strong>{t('freeShipping')}</strong>
                <span>{t('freeShippingOnOrders', { defaultValue: 'On orders over $50', ns: 'products' })}</span>
              </div>
            </div>
            <div className="pd-trust-item">
              <RotateCcw size={20} />
              <div>
                <strong>{t('easyReturns', { defaultValue: 'Easy Returns', ns: 'products' })}</strong>
                <span>{t('returnPolicy', { defaultValue: '30-day return policy', ns: 'products' })}</span>
              </div>
            </div>
            <div className="pd-trust-item">
              <Shield size={20} />
              <div>
                <strong>{t('securePayment', { defaultValue: 'Secure Payment', ns: 'products' })}</strong>
                <span>{t('protected', { defaultValue: '100% protected', ns: 'products' })}</span>
              </div>
            </div>
            <div className="pd-trust-item">
              <Clock size={20} />
              <div>
                <strong>{t('fastDelivery', { defaultValue: 'Fast Delivery', ns: 'products' })}</strong>
                <span>{t('deliveryTime', { defaultValue: '2-5 business days', ns: 'products' })}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Product Tabs */}
      <div className="pd-tabs-section">
        <div className="pd-tabs">
          <button 
            className={`pd-tab ${activeTab === 'description' ? 'active' : ''}`}
            onClick={() => setActiveTab('description')}
          >
            {t('description')}
          </button>
          <button 
            className={`pd-tab ${activeTab === 'specifications' ? 'active' : ''}`}
            onClick={() => setActiveTab('specifications')}
          >
            {t('specifications')}
          </button>
          <button 
            className={`pd-tab ${activeTab === 'reviews' ? 'active' : ''}`}
            onClick={() => setActiveTab('reviews')}
          >
            {t('reviews')} ({product.reviewCount || 0})
          </button>
          <button 
            className={`pd-tab ${activeTab === 'shipping' ? 'active' : ''}`}
            onClick={() => setActiveTab('shipping')}
          >
            {t('shippingReturns', { defaultValue: 'Shipping & Returns', ns: 'products' })}
          </button>
        </div>

        <div className="pd-tab-content">
          {activeTab === 'description' && (
            <div className="pd-description">
              <h3>{t('productDetails')}</h3>
              <p>{product.description || t('defaultDescription')}</p>
              {product.features && (
                <ul className="pd-features">
                  {product.features.map((feature, idx) => (
                    <li key={idx}><Check size={16} /> {feature}</li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {activeTab === 'specifications' && (
            <div className="pd-specs">
              <h3>{t('specifications')}</h3>
              <table className="pd-specs-table">
                <tbody>
                  {product.brand && <tr><td>{t('brand', { defaultValue: 'Brand', ns: 'common' })}</td><td>{product.brand}</td></tr>}
                  {selectedVariant?.sku && <tr><td>{t('sku', { defaultValue: 'SKU', ns: 'common' })}</td><td>{selectedVariant.sku}</td></tr>}
                  {!selectedVariant?.sku && product.sku && <tr><td>{t('sku', { defaultValue: 'SKU', ns: 'common' })}</td><td>{product.sku}</td></tr>}
                  <tr><td>{t('category', { defaultValue: 'Category', ns: 'search' })}</td><td>{product.category?.name || t('general', { defaultValue: 'General', ns: 'products' })}</td></tr>
                  {isVariableProduct && <tr><td>{t('productType', { defaultValue: 'Product Type', ns: 'products' })}</td><td>{t('variableProduct', { defaultValue: 'Variable (Multiple Variants)', ns: 'products' })}</td></tr>}
                  {!isVariableProduct && <tr><td>{t('productType', { defaultValue: 'Product Type', ns: 'products' })}</td><td>{t('simpleProduct', { defaultValue: 'Simple', ns: 'products' })}</td></tr>}
                  {product.weight && <tr><td>{t('weight', { defaultValue: 'Weight', ns: 'products' })}</td><td>{product.weight}</td></tr>}
                  {product.dimensions && <tr><td>{t('dimensions', { defaultValue: 'Dimensions', ns: 'products' })}</td><td>{product.dimensions}</td></tr>}
                  {product.material && <tr><td>{t('material', { defaultValue: 'Material', ns: 'products' })}</td><td>{product.material}</td></tr>}
                  {product.color && <tr><td>{t('color')}</td><td>{product.color}</td></tr>}
                  {product.size && <tr><td>{t('size')}</td><td>{product.size}</td></tr>}
                  {product.condition && <tr><td>{t('condition', { defaultValue: 'Condition', ns: 'products' })}</td><td>{product.condition}</td></tr>}
                  {product.warranty && <tr><td>{t('warranty')}</td><td>{product.warranty}</td></tr>}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'reviews' && (
            <div className="pd-reviews">
              <h3>{t('reviewsAndRatings')}</h3>
              <div className="pd-reviews-summary">
                <div className="pd-reviews-score">
                  <span className="pd-score">{(product.averageRating || 4.5).toFixed(1)}</span>
                  <div className="pd-score-stars">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        size={20}
                        fill={i < Math.floor(product.averageRating || 4.5) ? '#fbbf24' : 'none'}
                        stroke={i < Math.floor(product.averageRating || 4.5) ? '#fbbf24' : '#d1d5db'}
                      />
                    ))}
                  </div>
                  <span className="pd-score-count">{t('basedOnReviews', { count: product.reviewCount || 0, defaultValue: `Based on ${product.reviewCount || 0} reviews`, ns: 'products' })}</span>
                </div>
              </div>
              {!product.reviews?.length && (
                <p className="pd-no-reviews">{t('beFirstToReview')}</p>
              )}
            </div>
          )}

          {activeTab === 'shipping' && (
            <div className="pd-shipping">
              <h3>{t('shippingInformation', { defaultValue: 'Shipping Information', ns: 'products' })}</h3>
              <div className="pd-shipping-info">
                <div className="pd-shipping-item">
                  <Truck size={24} />
                  <div>
                    <h4>{t('standardShipping', { defaultValue: 'Standard Shipping', ns: 'products' })}</h4>
                    <p>{t('standardShippingDetails', { defaultValue: '5-7 business days • Free on orders over $50', ns: 'products' })}</p>
                  </div>
                </div>
                <div className="pd-shipping-item">
                  <Zap size={24} />
                  <div>
                    <h4>{t('expressShipping', { defaultValue: 'Express Shipping', ns: 'products' })}</h4>
                    <p>{t('expressShippingDetails', { defaultValue: '2-3 business days • $9.99', ns: 'products' })}</p>
                  </div>
                </div>
              </div>
              <h3>{t('returnPolicy', { defaultValue: 'Return Policy', ns: 'products' })}</h3>
              <p>{t('returnPolicyDetails', { defaultValue: 'We offer a 30-day return policy for all unused items in their original packaging. Items must be returned with all tags attached and in resalable condition.', ns: 'products' })}</p>
            </div>
          )}
        </div>
      </div>

    </div>
  );
};

export default ProductDisplay;
